/* NotificationsVeilComponent.js */

'use strict';
const EventEmitter = require('events').EventEmitter;
const _ = require('lodash');
const Renderer = require('./NotificationsVeilRenderer');
const ActionGroupEntity = require('@gluenetworks/platform-sdk-remote').entities.ws.ActionGroup;
/**
 *  NotificationsVeilComponent,
 *  provides a component with 3 checkboxes:
 *  notifyMe, notifySystemRecipientList, notifyOther.
 *  And input field, for user typed emails.
 *
 *  Input:
 *      logger              -    logger component
 *      userSession         -    userSession component
 *      options (optional)  -    Object { darkTheme: boolean, title: string, disabled: boolean, useNativeValidation: boolean, showErrorMessage: boolean, workType: string, subWorkType: string }
 *      values  (optional)  -    Object { notifyMe: string, notifyActionGroupDefault: string, notifyEmail: Array }
 *
 *  Output:
 *      For output use the subscription for events:
 *      * ValidationStateChanged -  boolean, indicates if validation errors are appeared, or cleared in the email input
 *      * EmailListWasChanged    -  workInput Object { notifyMe: string, notifyActionGroupDefault: string, notifyEmail: Array },
 *                                                                emitted every time the email list modified with the component.
 *      Or you can take current workInput Object with NotificationsVeilComponent.getNotificationsWorkInputObject()
 */
class NotificationsVeilComponent extends EventEmitter {
  constructor(logger, userSession, options, values, ControlUser, createdBy) {
    super();
    this._logger = logger;
    this._darkTheme = _.get(options, 'darkTheme', true);
    this._titleText = _.get(options, 'title', false);
    this._ControlUser = ControlUser;
    this._userSession = userSession;
    this._notifyMeEmail = undefined;
    this._scheduleCreator = createdBy;

    //game_center / model editor
    if (this.userSession.user) {
      this._userEmail = this.userSession._userEntity.email;
      this._tenantId = this.userSession.user.tenantId;
      this._userId = this.userSession.user.id;
      this._actionGroupId = this.userSession.user.actionGroupId;
      this._userName = this.userSession.user.userName;
    } else if (this.userSession.info) {
      this._userEmail = this.userSession.info.email;
      this._tenantId = this.userSession.info.organizationId;
      this._userId = this.userSession.info.userId;
      this._actionGroupId = this.userSession.info.actionGroupId;
      this._userName = this.userSession.info.userName;
    }

    this._actionGroup = undefined;

    this._disabled = _.get(options, 'disabled', false);
    this._useNativeValidation = _.get(options, 'useNativeValidation', false);
    this._showErrorMessage = _.get(options, 'showErrorMessage', true);
    this._notifyMe = _.get(values, 'notifyMe', null);
    this._notifyEmail = _.get(values, 'notifyEmail', null);
    this._notifyActionGroupDefault = _.get(values, 'notifyActionGroupDefault', null);

    const workType = _.get(options, 'workType'),
      subWorkType = _.get(options, 'subWorkType', null);
    this._emailType = this._mapWorkTypeToEmailScheme(workType, subWorkType);

    this._renderer = new Renderer(this);

    this._registerListeners();
    this._renderer.render();
  }

  /*******************************************/
  /* START PRIVATE COMPONENT MODEL METHODS   */
  /*******************************************/
  _registerListeners() {}

  _unregisterListeners() {}

  /*******************************************/
  /* END PRIVATE COMPONENT MODEL METHODS     */
  /* START PUBLIC COMPONENT MODEL METHODS    */
  /*******************************************/
  get renderer() {
    return this.getRenderer();
  }

  getRenderer() {
    return this._renderer;
  }

  get titleText() {
    return this._titleText;
  }

  get logger() {
    return this._logger;
  }

  get darkTheme() {
    return this._darkTheme;
  }

  get userSession() {
    return this._userSession;
  }

  get disabled() {
    return this._disabled;
  }

  get currentRecipients() {
    return this.renderer._currentRecipientsInput.options.map((recipientObj) => recipientObj.value);
  }

  set disabled(isDisabled) {
    this._disabled = isDisabled;
  }

  get useNativeValidation() {
    return this._useNativeValidation;
  }

  get showErrorMessage() {
    return this._showErrorMessage;
  }

  set notifyMe(value) {
    this._notifyMe = value;
  }

  set notifyEmail(value) {
    this._notifyEmail = value;
  }

  set notifyActionGroupDefault(value) {
    this._notifyActionGroupDefault = value;
  }

  get notifyMe() {
    return this._notifyMe;
  }

  get notifyEmail() {
    return this._notifyEmail;
  }

  get systemNotificationList() {
    let systemNotificationList = _.get(this.actionGroup, 'notifEmails.' + this.emailType, '');

    if (systemNotificationList.trim().length === 0) {
      systemNotificationList = _.get(this.actionGroup, 'notifEmails.' + 'global', '');
    }
    return systemNotificationList
      .split(',')
      .filter((email) => email.trim().length > 0)
      .map((email) => email.trim());
  }

  get notifyActionGroupDefault() {
    return this._notifyActionGroupDefault;
  }

  get emailType() {
    return this._emailType;
  }

  get actionGroup() {
    return this._actionGroup;
  }

  set actionGroup(newActionGroup) {
    this._actionGroup = newActionGroup;
  }

  get userEmail() {
    return this._userEmail;
  }

  get tenantId() {
    return this._tenantId;
  }

  get userId() {
    return this._userId;
  }

  get actionGroupId() {
    return this._actionGroupId;
  }

  isSystemNotificationListNotEmpty() {
    return this.systemNotificationList.length !== 0;
  }

  getNotificationsWorkInputObject() {
    let notificationsWorkInputObject = {};
    if (this.renderer._notifyMeCheckBox.value) {
      notificationsWorkInputObject.notifyMe = this._notifyMe;
    } else {
      notificationsWorkInputObject.notifyMe = null;
    }
    if (this.renderer._notifyOtherCheckBox.value && this.renderer._notifyOtherEmailInput.value) {
      notificationsWorkInputObject.notifyEmail = this.renderer._notifyOtherEmailInput.value
        .split(',')
        .map((email) => email.trim())
        .filter((email) => email.trim().length > 0);
    }
    if (this.renderer._notifySystemAdminCheckBox.value) {
      notificationsWorkInputObject.notifyActionGroupDefault = this.actionGroupId;
    }

    return notificationsWorkInputObject;
  }

  shutdown() {
    this._unregisterListeners();
    return this._renderer.shutdown().then(
      () => {
        this._userEmail = undefined;
        this._tenantId = undefined;
        this._userId = undefined;
        this._actionGroupId = undefined;
        this._userSession = undefined;
        this._emailType = undefined;
        this._notifyEmail = undefined;
        this._actionGroup = undefined;
        this._notifyActionGroupDefault = undefined;
        this._renderer = undefined;
      },
      (err) => {
        this._logger.warn({error: err}, 'NotificationsVeilComponent.shutdown() error');
      }
    );
  }

  /*******************************************/
  /* END COMPONENT MODEL METHODS             */
  /* START HANDLER METHODS                   */
  /*******************************************/
  whenModelIsReady() {
    let self = this;
    return ActionGroupEntity.loadAscendActionGroup(this.tenantId, this.actionGroupId, {
      id: true,
      name: true,
      notifEmails: true,
    })
      .then((currentUserActionGroup) => {
        self.actionGroup = currentUserActionGroup;
        return Promise.resolve();
      })
      .catch((err) => {
        this._logger.warn({error: err}, 'NotificationsVeilComponent.whenModelIsReady() error');
      });
  }

  /*******************************************/
  /* END HANDLER METHODS                     */
  /* START PRIVATE METHODS                   */
  /*******************************************/
  /**
   * @param {Object} workType
   * @param {Object} [subWorkType]
   * @returns {String} email type
   */
  _mapWorkTypeToEmailScheme(workType, subWorkType) {
    /*
            If subWorkType and emailType for it exists return emiltype
            If subWorkType doesnt exist or emailType for it doesnt exist get emailType for workType
            If emailType for workType also doesnt exist return global

        */
    const notifyEmailTypes = ActionGroupEntity.notifyEmailTypes;
    let emailType;
    if (subWorkType) {
      emailType = _.findKey(
        notifyEmailTypes,
        (emailType) => emailType.workType === workType && emailType.subWorkType === subWorkType
      );
    }

    if (!emailType || !subWorkType) {
      emailType =
        _.findKey(notifyEmailTypes, (emailType) => emailType.workType === workType) || 'global';
    }
    return emailType;
  }

  /*******************************************/
  /* END PRIVATE METHODS                     */
  /* START PUBLIC API METHODS                */
  /*******************************************/
  /**
   * Emits the current validation state
   * @param {boolean} isValid
   */
  _validationStateChanged(isValid) {
    this.emit('ValidationStateChanged', isValid);
  }

  /**
   * Emits the new notificationsWorkInput
   */
  _emailListWasChanged() {
    this.emit('EmailListWasChanged', this.getNotificationsWorkInputObject());
  }

  pushCreatorEmail(emailList, uid) {
    if (!this._ControlUser) {
      return new Promise((resolve) => {
        resolve(emailList);
      });
    }
    return this._ControlUser.loadAscendControlUser(this._tenantId, uid).then((user) => {
      emailList.push(user.email);
      this._notifyMeEmail = user.email;
      return Promise.resolve(emailList);
    });
  }

  /*******************************************/
  /* END PUBLIC API METHODS                  */
  /*******************************************/
}

module.exports = NotificationsVeilComponent;
